import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../../../context/AuthContext';
import { API_BASE_URL } from '../../../../../constants';

const useGuessWhoGame = (socket, roomId) => {
    const { t } = useTranslation();
    const { user } = useAuth(); // Integrate Auth Context

    // Data State
    const [originalPool, setOriginalPool] = useState([]);
    const [cardsData, setCardsData] = useState([]);
    const [skillsData, setSkillsData] = useState([]);
    const [aptitudesData, setAptitudesData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Game State
    const [board, setBoard] = useState([]);
    const [gameStatus, setGameStatus] = useState('waiting'); // waiting, picking, playing, game_over
    const [secretChar, setSecretChar] = useState(null);
    const [eliminatedIds, setEliminatedIds] = useState([]);
    const [playersOnline, setPlayersOnline] = useState(0);  // CRITICAL: Start at 0, wait for server sync
    const [winner, setWinner] = useState(null); // NEW: Track winner ID
    // Players State (changed from derived to state for authoritative updates)
    const [players, setPlayers] = useState([
        { name: `${user?.name || 'Unknown'}`, status: 'Online', isMe: true }
    ]);

    // NEW: Game State for turn system
    const [gameState, setGameState] = useState({
        status: 'waiting',     // waiting, picking, playing, game_over
        isMyTurn: false,
        turnNumber: 0,
        opponentReady: false,
        iAmReady: false,
        lastOpponentAction: null  // flip, guess, end_turn, null
    });

    // NEW: Opponent Stats (Flip/Unflip)
    const [opponentStats, setOpponentStats] = useState({ flipped: 0, unflipped: 24 });

    // NEW: Magic Question State
    const [magicAttempts, setMagicAttempts] = useState(3);

    // DEBUG: Log playersOnline changes
    useEffect(() => {
        const userId = localStorage.getItem('userId') || 'UNKNOWN';
        const timestamp = () => new Date().toTimeString().split(' ')[0];
        console.log(`[GuessWho][U${userId}][${timestamp()}] 🔢 playersOnline CHANGED: ${playersOnline}`);
        console.log(`[GuessWho][U${userId}][${timestamp()}] 👥 Current players list:`, players);
    }, [playersOnline, players]);

    // Effect to sync fallback players if we haven't received authoritative data yet
    useEffect(() => {
        setPlayers(prev => {
            const me = { name: `${user?.name || 'Unknown'}`, status: 'Online', isMe: true };

            // If we have authoritative data (detected by checking if any player has an ID), don't overwrite with heuristic
            const hasAuthoritativeData = prev.some(p => p.id !== undefined);
            if (hasAuthoritativeData) return prev;

            // Fallback heuristic logic (Legacy support) - others removed as unused
            return [
                me,
                ...Array(Math.max(0, playersOnline - 1)).fill(0).map((_, i) => ({ name: `Opponent ${i + 1}`, status: 'Online', isMe: false }))
            ];
        });
    }, [playersOnline, user]);
    // const [turn, setTurn] = useState(null); // 'my_turn' or 'opponent_turn' (TODO: Implement with User ID context)
    const [toast, setToast] = useState(null);
    const [pendingBoardEvent, setPendingBoardEvent] = useState(null); // Queue for board_shared events received before pool loads

    // Action Mode: 'flip' or 'guess'
    const [actionMode, setActionMode] = useState('flip');

    // NEW: Guess Confirmation State (Replaces window.confirm)
    const [guessCandidate, setGuessCandidate] = useState(null);

    // --- HELPER FUNCTIONS ---
    const showToast = useCallback((msg, type = 'success') => {
        setToast({ msg, type });
    }, []);

    const resetGame = useCallback((currentBoard) => {
        setGameStatus('picking');
        setSecretChar(null);
        setEliminatedIds([]);
        setGuessCandidate(null); // Reset confirmation
    }, []);

    // --- SOCKET EVENTS ---
    useEffect(() => {
        // Use user.user_id if available, fallback to localStorage, default to 'UNKNOWN'
        const userId = user?.user_id || localStorage.getItem('userId') || 'UNKNOWN';
        const timestamp = () => new Date().toISOString().split('T')[1].slice(0, -1);

        console.log(`[GuessWho][U${userId}][${timestamp()}] 🔧 useEffect MOUNTING - Registering listeners`);
        console.log(`[GuessWho][U${userId}][${timestamp()}] State: socket=${!!socket}, roomId=${roomId}, board.length=${board.length}, pool.length=${originalPool.length}`);

        if (!socket) {
            console.log(`[GuessWho][U${userId}][${timestamp()}] ❌ No socket, skipping listener registration`);
            return;
        }

        // Listener: board_shared
        socket.on('guesswho:board_shared', (data) => {
            console.log(`[GuessWho][U${userId}][${timestamp()}] 📨 EVENT RECEIVED: guesswho:board_shared`, data);

            // CRITICAL: Do NOT update playersOnline here - this event can be received by the host alone
            // The board is generated and shared BEFORE the second player joins
            // playersOnline should ONLY be updated from room_details event
            console.log(`[GuessWho][U${userId}][${timestamp()}] ⚠️ NOT updating playersOnline (board_shared doesn't mean 2 players)`);

            if (data.success && data.board) {
                console.log(`[GuessWho][U${userId}][${timestamp()}] ✅ Board data valid`);
                console.log(`[GuessWho][U${userId}][${timestamp()}] Board IDs received:`, data.board);
                console.log(`[GuessWho][U${userId}][${timestamp()}] Current originalPool.length:`, originalPool.length);

                // If pool not loaded yet, QUEUE the event for later processing
                if (originalPool.length < 24) {
                    console.warn(`[GuessWho][U${userId}][${timestamp()}] ⚠️ POOL NOT READY - Queueing event for later processing`);
                    setPendingBoardEvent(data);
                    return;
                }

                // Pool is ready, process immediately
                console.log(`[GuessWho][U${userId}][${timestamp()}] 🔄 Processing board immediately (pool ready)`);
                const boardChars = data.board.map(id => {
                    const found = originalPool.find(c => c.id === id);
                    if (!found) {
                        console.warn(`[GuessWho][U${userId}][${timestamp()}] ⚠️ ID ${id} not found in pool`);
                    }
                    return found;
                }).filter(c => c);

                console.log(`[GuessWho][U${userId}][${timestamp()}] Matched characters: ${boardChars.length}/24`);

                if (boardChars.length === 24) {
                    console.log(`[GuessWho][U${userId}][${timestamp()}] ✅ All 24 matched, setting board`);
                    setBoard(boardChars);
                    resetGame(boardChars);
                    showToast('Board received from host!', 'success');
                } else {
                    console.error(`[GuessWho][U${userId}][${timestamp()}] ❌ Only ${boardChars.length} characters matched`);
                }
            } else {
                console.error(`[GuessWho][U${userId}][${timestamp()}] ❌ Invalid board data:`, data);
            }
        });


        // Listener: room_details (New Source of Truth)
        socket.on('room_details', (data) => {
            console.log(`[GuessWho][U${userId}][${timestamp()}] 📨 EVENT RECEIVED: room_details`, data);
            if (data.success && data.data) {
                const { host_id, players: playersData } = data.data;

                // Update Players List based on authoritative structured data
                setPlayers(prev => {
                    // Try to get the latest ID from storage (synced by useGameRoom)
                    const outputUserId = localStorage.getItem('userId');
                    const currentUserId = outputUserId ? parseInt(outputUserId) : parseInt(userId);
                    const hostIdInt = parseInt(host_id);

                    console.log(`[GuessWho] 🆔 Identity Check: Me=${currentUserId}, Host=${hostIdInt}`);
                    console.log(`[GuessWho] 👥 Players Data:`, playersData);

                    // NEW: playersData is now an array of objects: [{user_id, username, role, status, ...}]
                    const newPlayers = playersData.map(playerObj => {
                        const pidInt = parseInt(playerObj.user_id);
                        const isMe = pidInt === currentUserId;
                        const isHost = playerObj.role === 'host';

                        // Use username from backend, fallback to user context if it's me
                        let displayName = playerObj.username || `Player ${pidInt}`;
                        if (isMe && user?.name) {
                            displayName = user.name;
                        }

                        // Add role label
                        if (isHost) {
                            displayName = `${displayName} (Host)`;
                        }

                        return {
                            name: displayName,
                            status: playerObj.status === 'online' ? 'Online' : 'Offline',
                            isMe: isMe,
                            isHost: isHost,
                            id: pidInt
                        };
                    });

                    console.log(`[GuessWho] ✅ Mapped Players:`, newPlayers);
                    return newPlayers;
                });

                // Update presence count
                console.log(`[GuessWho][U${userId}][${timestamp()}] 🔄 UPDATING playersOnline: OLD=${playersOnline} → NEW=${playersData.length}`);
                setPlayersOnline(playersData.length);
                console.log(`[GuessWho][U${userId}][${timestamp()}] ✅ setPlayersOnline called with: ${playersData.length}`);
            } else {
                console.log(`[GuessWho][U${userId}][${timestamp()}] ⚠️ room_details arrived but data is invalid:`, data);
            }
        });

        // Listener: user_joined_room
        socket.on('user_joined_room', (data) => {
            console.log(`[GuessWho][U${userId}][${timestamp()}] 📨 ===== EVENT: user_joined_room RECEIVED =====`);
            console.log(`[GuessWho][U${userId}][${timestamp()}] 📥 Data:`, JSON.stringify(data, null, 2));
            console.log(`[GuessWho][U${userId}][${timestamp()}] Current state: boardLength=${board.length}, poolLength=${originalPool.length}`);
            console.log(`[GuessWho][U${userId}][${timestamp()}] Current playersOnline: ${playersOnline}`);
            console.log(`[GuessWho][U${userId}][${timestamp()}] Current players:`, players);

            // CRITICAL: Do NOT update playersOnline here - wait for room_details which has authoritative Redis data
            console.log(`[GuessWho][U${userId}][${timestamp()}] ⚠️ NOT updating playersOnline from user_joined_room (will wait for room_details)`);

            // Add new player to list (Optimistic update or Trigger Refetch)
            // Ideally, we trigger a refetch of details to be sure
            if (roomId) {
                console.log(`[GuessWho][U${userId}][${timestamp()}] 🔄 Requesting updated room_details after join`);
                socket.emit('get_room_details', { room_id: roomId });
            }

            showToast(`🎉 ${data.username || 'Player'} joined the room!`, 'success');

            // AUTO-GENERATE BOARD when second player joins (only host generates)
            // NOTE: We can't rely on data.player_count here anymore - wait for room_details update
            console.log(`[GuessWho][U${userId}][${timestamp()}] ⏸️ Skipping auto-generation logic (will trigger after room_details updates)`);
        });

        // NEW: Listener for player_ready
        socket.on('guesswho:player_ready', (data) => {
            console.log(`[GuessWho][U${userId}][${timestamp()}] 📨 EVENT RECEIVED: guesswho:player_ready`, data);
            const myUserId = parseInt(localStorage.getItem('userId'));

            if (data.user_id !== myUserId) {
                setGameState(prev => ({ ...prev, opponentReady: true }));
                showToast('Opponent picked their character!', 'info');
                console.log(`[GuessWho][U${userId}][${timestamp()}] ✅ Opponent ready`);
            } else {
                setGameState(prev => ({ ...prev, iAmReady: true }));
                console.log(`[GuessWho][U${userId}][${timestamp()}] ✅ I am ready`);
            }
        });

        socket.on('guesswho:game_started', (data) => {
            console.log(`[GuessWho][U${userId}][${timestamp()}] 📨 EVENT RECEIVED: guesswho:game_started`, data);
            if (data.success && data.data) {
                // BUG FIX: Only set board if we don't have one, or if ours is empty.
                if (board.length === 0 && data.data.board && data.data.board.length > 0) {
                    console.log(`[GuessWho] 📥 Setting board from game_started event`);
                    setBoard(data.data.board);
                } else {
                    console.log(`[GuessWho] 🛡️ Board already set (len=${board.length}), ignoring board from game_started`);
                }

                if (data.data.your_character) {
                    setSecretChar(data.data.your_character);
                    setGameStatus('playing');
                }


                // Update game state from server (personalized for this player)
                if (data.game_state) {
                    setGameState(prev => ({
                        ...prev,
                        status: data.game_state.status || 'playing',
                        isMyTurn: data.game_state.is_my_turn || false,
                        turnNumber: data.game_state.turn_number ?? 0,  // Use ?? to preserve 0
                        opponentReady: data.game_state.opponent_ready || true,
                        iAmReady: data.game_state.i_am_ready || true
                    }));
                    console.log(`[GuessWho][U${userId}][${timestamp()}] 🎮 Game state synced from server (is_my_turn=${data.game_state.is_my_turn})`);
                } else {
                    console.warn(`[GuessWho][U${userId}] ⚠️ No game_state found in game_started event`);
                }


                showToast('Game Started!', 'success');
            }
        });



        // NEW: Updated turn_changed listener
        socket.on('guesswho:turn_changed', (data) => {
            console.log(`[GuessWho][U${userId}][${timestamp()}] 📨 EVENT RECEIVED: guesswho:turn_changed`, data);
            const myUserId = parseInt(localStorage.getItem('userId'));
            const isMyTurn = data.next_player_id === myUserId;

            // --- NEW: UPDATE OPPONENT STATS ---
            if (data.last_player_stats) {
                const stats = data.last_player_stats;
                // If the stats usage belongs to the OTHER player (not me), update my view of them
                if (stats.player_id !== myUserId) {
                    console.log(`[GuessWho] 📊 Updating Opponent Stats:`, stats);
                    setOpponentStats({
                        flipped: stats.flipped_count,
                        unflipped: stats.unflipped_count
                    });
                }
            }

            setGameState(prev => ({
                ...prev,
                isMyTurn,
                turnNumber: data.turn_number || prev.turnNumber + 1,
                lastOpponentAction: isMyTurn ? null : prev.lastOpponentAction  // Clear if my turn
            }));

            if (isMyTurn) {
                showToast('Your turn!', 'success');
                console.log(`[GuessWho][U${userId}][${timestamp()}] ✅ MY TURN (Turn #${data.turn_number})`);
            } else {
                setActionMode('flip'); // FORCE FLIP MODE if not my turn
                showToast('Opponent\'s turn', 'info');
                console.log(`[GuessWho][U${userId}][${timestamp()}] ⏳ OPPONENT'S TURN (Turn #${data.turn_number})`);
            }
        });

        // NEW: Listener for game state updates (reconnection/sync)
        socket.on('guesswho:game_state_update', (data) => {
            console.log(`[GuessWho][U${userId}][${timestamp()}] 📨 EVENT RECEIVED: guesswho:game_state_update`, data);
            if (data.success && data.game_state) {
                const state = data.game_state;
                setGameState(prev => ({
                    ...prev,
                    status: state.status || prev.status,
                    isMyTurn: state.is_my_turn || false,
                    turnNumber: state.turn_number ?? prev.turnNumber,  // Use ?? to preserve 0
                    opponentReady: state.opponent_ready || false,
                    iAmReady: state.i_am_ready || false
                }));
                console.log(`[GuessWho][U${userId}][${timestamp()}] 🔄 Game state refreshed:`, state);
            }
        });

        socket.on('guesswho:game_over', (data) => {
            console.log(`[GuessWho][U${userId}][${timestamp()}] 📨 EVENT RECEIVED: guesswho:game_over`, data);
            setGameStatus('game_over');

            const myUserId = parseInt(localStorage.getItem('userId'));
            const winnerId = parseInt(data.winner);

            setWinner(winnerId); // NEW: Store winner for UI

            if (winnerId === myUserId) {
                showToast('🏆 YOU WIN! 🏆', 'success');
            } else {
                showToast(`💀 GAME OVER - Winner: Player ${winnerId}`, 'error');
            }
        });

        // NEW: Listener for Magic Question Result
        socket.on('guesswho:win_possibility_result', (data) => {
            console.log(`[GuessWho][U${userId}][${timestamp()}] 📨 EVENT RECEIVED: guesswho:win_possibility_result`, data);

            // 1. Update local counter
            if (data.remaining_attempts !== undefined) {
                setMagicAttempts(data.remaining_attempts);
            }

            // 2. Show Toast with Translation Key
            // Key format: "GuessWho.magic_result.BOTH_CAN_WIN"
            // Fallback: data.message ("Los 2 pueden ganar")
            const messageKey = `GuessWho.magic_result.${data.result}`;
            showToast(t(messageKey, data.message), 'info');
        });

        // NEW: Listener for Guess Result (to update opponent stats)
        socket.on('guesswho:guess_result', (data) => {
            console.log(`[GuessWho][U${userId}][${timestamp()}] 📨 EVENT RECEIVED: guesswho:guess_result`, data);

            const myUserId = parseInt(localStorage.getItem('userId'));

            // Only process if this guess was NOT made by me
            if (data.player_id && data.player_id !== myUserId) {
                // Update opponent's card count
                setOpponentStats(prev => ({
                    flipped: prev.flipped + 1,
                    unflipped: Math.max(0, prev.unflipped - 1)
                }));

                console.log(`[GuessWho][U${userId}] 📊 Opponent eliminated card ${data.guessed}`);
            }
        });

        console.log(`[GuessWho][U${userId}][${timestamp()}] ✅ All listeners registered`);

        // Request existing board and room details if component mounts late
        if (roomId) {
            // Fetch Room Details (Host, Players) - NEW
            console.log(`[GuessWho][U${userId}][${timestamp()}] 📤 EMITTING: get_room_details`);
            socket.emit('get_room_details', { room_id: roomId });

            if (board.length === 0) {
                console.log(`[GuessWho][U${userId}][${timestamp()}] 📤 EMITTING: guesswho:request_board (late mount recovery)`);
                socket.emit('guesswho:request_board', { room_id: roomId });
            } else {
                console.log(`[GuessWho][U${userId}][${timestamp()}] ⏭️ Skipping request_board (board.length=${board.length}, roomId=${roomId})`);
            }
        }

        return () => {
            console.log(`[GuessWho][U${userId}][${timestamp()}] 🧹 useEffect UNMOUNTING - Removing listeners`);
            socket.off('guesswho:board_shared');
            socket.off('room_details'); // Remove new listener
            socket.off('user_joined_room');
            socket.off('guesswho:game_started');
            socket.off('guesswho:turn_changed');
            socket.off('guesswho:game_over');
            socket.off('guesswho:guess_result');
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [socket, board.length, roomId, showToast, resetGame]); // REMOVED originalPool to prevent unmount/remount race condition

    // --- AUTO-GENERATE BOARD WHEN POOL LOADS (Primary Generation) ---
    useEffect(() => {
        const userId = localStorage.getItem('userId') || 'UNKNOWN';
        const timestamp = () => new Date().toTimeString().split(' ')[0];

        // Only attempt if:
        // 1. Room exists
        // 2. Pool just loaded (length >= 24)
        // 3. Board is still empty
        // 4. We are the HOST (or first player) - we can't easily know this without backend auth, 
        // IMPORTANT: Generate board as soon as pool loads so it can be sent to Redis/backend
        // The VISIBILITY of the board is controlled by the UI component based on playersOnline
        // This ensures the second player receives the board when joining

        if (!roomId || originalPool.length < 24 || board.length > 0 || pendingBoardEvent) {
            return;
        }

        console.log(`[GuessWho][U${userId}][${timestamp()}] 🔄 Pool loaded, generating board for Redis storage`);
        console.log(`[GuessWho][U${userId}][${timestamp()}] State: roomId=${roomId}, pool.length=${originalPool.length}, board.length=${board.length}, playersOnline=${playersOnline}`);

        // Generate board (this happens even with 1 player so it can be stored)
        const shuffled = [...originalPool];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        const selected = shuffled.slice(0, 24);
        console.log(`[GuessWho][U${userId}][${timestamp()}] 🎲 Generated board (for storage):`, selected.map(c => c.id));

        setBoard(selected);
        resetGame(selected);

        // Share with backend/room IMMEDIATELY (critical for second player)
        if (socket) {
            const boardIds = selected.map(c => c.id);
            console.log(`[GuessWho][U${userId}][${timestamp()}] 📤 EMITTING: guesswho:share_board (sending to Redis)`, { room_id: roomId, board: boardIds });
            socket.emit('guesswho:share_board', {
                room_id: roomId,
                board: boardIds
            });
            console.log(`[GuessWho][U${userId}][${timestamp()}] ✅ Board sent to server for storage`);

            showToast('Board generated! Waiting for opponent...', 'info');
        }
    }, [originalPool, roomId, board.length, socket, resetGame, showToast, pendingBoardEvent, playersOnline]);





    // --- PROCESS PENDING BOARD_SHARED EVENTS WHEN POOL LOADS ---
    useEffect(() => {
        const userId = localStorage.getItem('userId') || 'UNKNOWN';
        const timestamp = () => new Date().toTimeString().split(' ')[0];

        // Only process if:
        // 1. Pool is now loaded
        // 2. There's a pending board event
        // 3. Board is still empty (hasn't been set yet)
        if (originalPool.length < 24 || !pendingBoardEvent || board.length > 0) {
            return;
        }

        console.log(`[GuessWho][U${userId}][${timestamp()}] 🔄 POOL NOW LOADED - Processing queued board_shared event`);
        console.log(`[GuessWho][U${userId}][${timestamp()}] Pool length: ${originalPool.length}`);
        console.log(`[GuessWho][U${userId}][${timestamp()}] Board IDs from queue:`, pendingBoardEvent.board);

        // Process the pending event
        const boardChars = pendingBoardEvent.board.map(id => {
            const found = originalPool.find(c => c.id === id);
            if (!found) {
                console.warn(`[GuessWho][U${userId}][${timestamp()}] ⚠️ ID ${id} not found in pool`);
            }
            return found;
        }).filter(c => c);

        console.log(`[GuessWho][U${userId}][${timestamp()}] Matched characters: ${boardChars.length}/24`);

        if (boardChars.length === 24) {
            console.log(`[GuessWho][U${userId}][${timestamp()}] ✅ All 24 matched from queued event, setting board`);
            setBoard(boardChars);
            resetGame(boardChars);
            showToast('Board received from host!', 'success');
            setPendingBoardEvent(null); // Clear the queue
            console.log(`[GuessWho][U${userId}][${timestamp()}] 📤 RECIBÍ LA POOL Y PROCESÉ EL TABLERO CORRECTAMENTE`);
        } else {
            console.error(`[GuessWho][U${userId}][${timestamp()}] ❌ Only ${boardChars.length} characters matched from queue`);
        }
    }, [originalPool, pendingBoardEvent, board.length, resetGame, showToast]);

    // --- FETCH DATA (Legacy/Hybrid) ---
    useEffect(() => {
        const fetchData = async () => {
            try {
                const [charsRes, cardsRes, skillsRes, aptsRes] = await Promise.all([
                    fetch(`${API_BASE_URL}/characters`),
                    fetch(`${API_BASE_URL}/cards`),
                    fetch(`${API_BASE_URL}/skills`),
                    fetch(`${API_BASE_URL}/aptitudes`)
                ]);

                const charsData = await charsRes.json();
                const cardsRaw = await cardsRes.json();
                const skillsRaw = await skillsRes.json();
                const aptsRaw = await aptsRes.json();

                setCardsData(cardsRaw);
                setSkillsData(skillsRaw);
                setAptitudesData(aptsRaw);

                const pool = charsData
                    .filter(c => c.Player_type === 'PC')
                    .map(char => {
                        const card = cardsRaw.find(cd => cd.Id_Personaje === char.Id_Personaje && cd.reference === 'Original');
                        if (!card) return null;

                        const characterName = char.name_en.replace(/[\\/*?:"<>|]/g, '').replace(/ /g, '_');
                        const reference = 'Original';
                        const costumeName = card.title_en.replace(/[\\/*?:"<>|]/g, '').replace(/ /g, '_');
                        const imageName = `${characterName}_${reference}_${costumeName}.png`;
                        const imgUrl = `${API_BASE_URL}/api/thumb?img=/images/character/${imageName}&width=256`;

                        return {
                            id: char.Id_Personaje,
                            name: char.name_en,
                            img: imgUrl,
                            data: char
                        };
                    })
                    .filter(item => item !== null);

                setOriginalPool(pool);
                setLoading(false);
            } catch (err) {
                console.error(err);
                setError(t('GuessWho.error_load', 'Failed to load character data'));
                setLoading(false);
            }
        };

        fetchData();
    }, [t]);

    // --- TOAST ---
    useEffect(() => {
        if (toast) {
            const timer = setTimeout(() => setToast(null), 3000);
            return () => clearTimeout(timer);
        }
    }, [toast]);



    // --- GAME ACTIONS ---



    // --- INTERACTION HANDLER ---

    const handleCardClick = (charId) => {
        // Phase 1: Picking Secret Character
        if (gameStatus === 'picking') {
            const char = board.find(c => c.id === charId);
            setSecretChar(char);
            setGameStatus('playing');

            // RESTORED: Emit Ready immediately (Fix for Secret: None bug)
            console.log(`[GuessWho] 📤 Emitting ready with charId: ${charId}`);
            socket?.emit('guesswho:ready', {
                room_id: roomId,
                secret_character_id: charId
            });
            showToast(t('GuessWho.toast.character_selected', 'Character Selected!'), 'info');
            return;
        }

        // Phase 2: Playing
        if (gameStatus === 'playing') {
            if (actionMode === 'guess') {
                // REPLACED: window.confirm -> setGuessCandidate (Fix 12.3)
                const char = board.find(c => c.id === charId);
                setGuessCandidate(char);
            } else {
                // FLIP MODE
                toggleElimination(charId);
            }
        }
    };

    const toggleElimination = (charId) => {
        // Optimistic UI update
        const isEliminated = eliminatedIds.includes(charId);
        let newEliminated;

        if (isEliminated) {
            newEliminated = eliminatedIds.filter(id => id !== charId);
        } else {
            newEliminated = [...eliminatedIds, charId];
        }
        setEliminatedIds(newEliminated);

        // Emit event
        socket?.emit('guesswho:flip_card', {
            room_id: roomId,
            card_id: charId
        });
    };

    // NEW: Confirm the guess (triggered by Modal)
    const confirmGuess = () => {
        const charId = guessCandidate?.id; // Capture ID immediately
        if (!charId) return;

        if (!gameState.isMyTurn) {
            showToast('Wait for your turn!', 'error');
            setGuessCandidate(null);
            return;
        }

        console.log('[GuessWho] 🟢 User confirmed guess for:', charId);

        // 1. Force Visual Flip Immediately (Optimistic)
        setEliminatedIds(prev => {
            if (!prev.includes(charId)) {
                console.log('[GuessWho] 🔄 Optimistic Flip added:', charId);
                return [...prev, charId];
            }
            return prev;
        });

        // 2. Emit Flip Event (Sync with Backend)
        socket?.emit('guesswho:flip_card', {
            room_id: roomId,
            card_id: charId
        });

        // 3. Emit Guess Event
        socket?.emit('guesswho:make_guess', {
            room_id: roomId,
            character_id: charId
        });

        showToast('Guess submitted...', 'info');
        setGuessCandidate(null); // Close modal
    };

    // NEW: Cancel the guess
    const cancelGuess = () => {
        setGuessCandidate(null);
    };

    const handleEndTurn = () => {
        // --- PHASE 2: BATCH SYNC ---
        // 1. Get Flipped (Eliminated) IDs
        const flipped = [...eliminatedIds];

        // 2. Calculate Unflipped (Remaining) IDs
        const unflipped = board
            .map(c => c.id)
            .filter(id => !eliminatedIds.includes(id));

        console.log('[GuessWho] 📤 Ending Turn with Sync:', { flipped_count: flipped.length, unflipped_count: unflipped.length });

        socket?.emit('guesswho:end_turn', {
            room_id: roomId,
            flipped_card_ids: flipped,
            unflipped_card_ids: unflipped
        });
        showToast('Turn passed.', 'info');
    };
    // Check win possibility
    const checkWinPossibility = useCallback(() => {
        if (magicAttempts <= 0) {
            showToast(t('GuessWho.magic_result.LIMIT_REACHED', 'No attempts remaining'), 'error');
            return;
        }
        if (!socket || !roomId) return;

        // SEND CURRENT ELIMINATED IDS TO BACKEND
        // Fixes "Stale Data" bug (Backend reading old Redis state)
        // We send the source of truth (Frontend State) to the backend for validation

        console.log('[GuessWho] ❓ Requesting Win Possibility Check...');
        console.log('[GuessWho] 📦 Sending Eliminated IDs:', eliminatedIds);

        socket.emit('guesswho:check_win_possibility', {
            room_id: roomId,
            eliminated_ids: eliminatedIds // <--- CRITICAL FIX
        });
    }, [socket, roomId, eliminatedIds, magicAttempts, showToast, t]);

    return {
        // State
        loading,
        error,
        board,
        gameStatus,
        secretChar,
        eliminatedIds,
        toast,
        playersOnline,
        players,
        actionMode,
        cardsData,
        skillsData,
        aptitudesData,
        opponentStats, // NEW

        gameState,  // NEW: Turn system state
        winner,     // NEW: Expose winner

        // Actions (Mapped/Aliased to fix ESLint 'not defined' errors)
        toggleEliminated: toggleElimination,
        confirmSelection: () => console.log('confirmSelection is deprecated'),
        endTurn: handleEndTurn,
        checkWinPossibility,
        setSecretChar,
        surrenderGame: () => console.log('surrenderGame not implemented'),
        initDebug: () => console.log('initDebug not implemented'),

        // Original Actions (Directly used by Board)
        setActionMode,
        handleCardClick,
        handleEndTurn,
        handleCheckWin: checkWinPossibility, // Alias for backward compatibility
        magicAttempts,

        // NEW: Modal Actions
        guessCandidate,
        confirmGuess,
        cancelGuess
    };
};

export default useGuessWhoGame;
